import { NextResponse } from 'next/server';
import { sourceLanguageTag, isAvailableLanguageTag, availableLanguageTags } from '$paraglide/runtime.js';
import { addSeoHeaders } from './headers.js';
import { LANG_COOKIE, PARAGLIDE_LANGUAGE_HEADER_NAME } from '../constants.js';
import { createCookieDetection } from './detection/CookieDetection.js';
import { createAcceptLanguageDetection } from './detection/AcceptLanguageDetection.js';

const middlewareOptionDefaults = {
    redirect: true,
};
function Middleware(opt) {
    opt = { ...middlewareOptionDefaults, ...opt };
    const cookieDetection = createCookieDetection({
        availableLanguageTags: availableLanguageTags,
    });
    const acceptLanguageDetection = createAcceptLanguageDetection({
        availableLanguageTags: availableLanguageTags,
    });
    const languageDetectors = [opt.strategy.resolveLocale, cookieDetection, acceptLanguageDetection];
    /**
     * Detects the language that should be used for the request based on the
     * routing strategy and language negotiation.
     *
     * @param request
     * @returns
     */
    function detectLanguage(request) {
        for (const detector of languageDetectors) {
            const locale = detector(request);
            if (locale)
                return locale;
        }
        return sourceLanguageTag;
    }
    /**
     * Creates an appropriate routing response for the given request
     * and language.
     *
     * @param request - The request to respond to
     * @param locale - The detected language
     */
    function getResponse(request, locale) {
        const decodedPathname = decodeURI(request.nextUrl.pathname);
        const canonicalPath = opt.strategy.getCanonicalPath(decodedPathname, locale);
        const localisedURL = opt.strategy.getLocalisedUrl(canonicalPath, locale, false);
        const localeCookieValue = request.cookies.get(LANG_COOKIE.name)?.value;
        const localeCookieMatches = isAvailableLanguageTag(localeCookieValue) && localeCookieValue === locale;
        const headers = new Headers(request.headers);
        headers.set(PARAGLIDE_LANGUAGE_HEADER_NAME, locale);
        const shouldRedirect = opt.redirect && localisedURL.pathname !== decodedPathname;
        const rewriteRequired = request.nextUrl.pathname !== canonicalPath;
        const response = shouldRedirect
            ? redirect(request.nextUrl, localisedURL.pathname, { headers })
            : rewriteRequired
                ? rewrite(request.nextUrl, canonicalPath, { request: { headers } })
                : NextResponse.next({ request: { headers } });
        // Update the locale-cookie
        if (!localeCookieMatches) {
            response.cookies.set(LANG_COOKIE.name, locale, {
                sameSite: LANG_COOKIE.SameSite,
                maxAge: LANG_COOKIE["Max-Age"],
                path: request.nextUrl.basePath || "/",
            });
        }
        addSeoHeaders(response.headers, {
            availableLanguageTags: availableLanguageTags,
            canonicalPath,
            request,
            strategy: opt.strategy,
        });
        return response;
    }
    /**
     * Middleware for handling language detection, redirects and language cookies
     *
     * https://nextjs.org/docs/pages/building-your-application/routing/middleware#setting-headers
     */
    function middleware(request) {
        const locale = detectLanguage(request);
        return getResponse(request, locale);
    }
    middleware.detectLanguage = detectLanguage;
    middleware.getResponse = getResponse;
    return middleware;
}
const rewrite = (nextUrl, pathname, init) => {
    const destination = nextUrl.clone();
    destination.pathname = pathname;
    return NextResponse.rewrite(destination, init);
};
const redirect = (nextUrl, pathname, init) => {
    const destination = nextUrl.clone();
    destination.pathname = pathname;
    return NextResponse.redirect(destination, init);
};

export { Middleware };
